/*
 * Copyright (C) 2020 Graylog, Inc.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the Server Side Public License, version 1,
 * as published by MongoDB, Inc.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * Server Side Public License for more details.
 *
 * You should have received a copy of the Server Side Public License
 * along with this program. If not, see
 * <http://www.mongodb.com/licensing/server-side-public-license>.
 */
import { useCallback, useContext, useState } from 'react';

import { useStore } from 'stores/connect';
import Store from 'logic/local-storage/Store';
import { CurrentUserStore } from 'stores/users/CurrentUserStore';
import { PreferencesStore } from 'stores/users/PreferencesStore';

import type { ThemeMode } from './constants';
import { PREFERENCES_THEME_MODE, DEFAULT_THEME_MODE, THEME_MODE_DARK, THEME_MODE_LIGHT } from './constants';

import UserPreferencesContext from '../contexts/UserPreferencesContext';
import usePrefersColorScheme from '../hooks/usePrefersColorScheme';

type CurrentUser = {
  currentUser?: {
    username: string;
    read_only: boolean;
  };
};

const storeThemePreference = () => {
  const preference = Store.get(PREFERENCES_THEME_MODE);

  return preference === THEME_MODE_LIGHT || preference === THEME_MODE_DARK ? preference : null;
};

const _getInitialThemeMode = (userPreferences, browserThemePreference, initialThemeModeOverride) => {
  if (initialThemeModeOverride) {
    return initialThemeModeOverride;
  }

  const userThemePreference = userPreferences[PREFERENCES_THEME_MODE] ?? storeThemePreference();

  return userThemePreference ?? browserThemePreference ?? DEFAULT_THEME_MODE;
};

const useCurrentThemeMode = (initialThemeModeOverride: ThemeMode): [ThemeMode, (newThemeMode: ThemeMode) => void] => {
  const browserThemePreference = usePrefersColorScheme();

  const { userIsReadOnly, username } = useStore(CurrentUserStore, (userStore) => ({
    username: (userStore as CurrentUser)?.currentUser?.username,
    userIsReadOnly: (userStore as CurrentUser)?.currentUser?.read_only ?? true,
  }));

  const userPreferences = useContext(UserPreferencesContext);
  const initialThemeMode = _getInitialThemeMode(userPreferences, browserThemePreference, initialThemeModeOverride);
  const [currentThemeMode, setCurrentThemeMode] = useState<ThemeMode>(initialThemeMode);

  const changeCurrentThemeMode = useCallback((newThemeMode: ThemeMode) => {
    setCurrentThemeMode(newThemeMode);
    Store.set(PREFERENCES_THEME_MODE, newThemeMode);

    if (!userIsReadOnly) {
      const nextPreferences = { ...userPreferences, [PREFERENCES_THEME_MODE]: newThemeMode };

      PreferencesStore.saveUserPreferences(username, nextPreferences);
    }
  }, [userIsReadOnly, userPreferences, username]);

  return [currentThemeMode, changeCurrentThemeMode];
};

export default useCurrentThemeMode;
